import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Save, RefreshCw, Eye, Plus, Trash2 } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSHeroPage = () => {
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadHero();
  }, []);

  const loadHero = async () => {
    try {
      const response = await api.get('/cms/hero');
      setHero(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/hero', hero);
      toast.success('Section Hero mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addStat = () => {
    setHero({
      ...hero,
      stats: [...(hero.stats || []), { value: '', label: '' }]
    });
  };

  const removeStat = (index) => {
    const newStats = [...hero.stats];
    newStats.splice(index, 1);
    setHero({ ...hero, stats: newStats });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Section Hero</h1>
          <p className="text-slate-500">Personnalisez la section d'accueil du site</p>
        </div>
        <Button onClick={() => window.open('/site', '_blank')} variant="outline">
          <Eye className="w-4 h-4 mr-2" />
          Prévisualiser
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contenu principal</CardTitle>
          <CardDescription>Titre, sous-titre et appels à l'action</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hero && (
            <>
              <div className="space-y-2">
                <Label>Titre principal</Label>
                <Input
                  value={hero.title || ''}
                  onChange={(e) => setHero({ ...hero, title: e.target.value })}
                  placeholder="Boostons la **visibilité** de votre entreprise !"
                />
                <p className="text-xs text-slate-500">
                  💡 Utilisez **mot** pour mettre un mot en surbrillance colorée (dégradé cyan/orange)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Sous-titre</Label>
                <Textarea
                  value={hero.subtitle || ''}
                  onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
                  rows={2}
                  placeholder="Logos, visuels, vidéos... Une équipe de **passionnés** pour vos projets."
                />
                <p className="text-xs text-slate-500">
                  💡 Utilisez **mot** pour mettre un mot en cyan gras
                </p>
              </div>

              <div className="space-y-2">
                <Label>URL de la vidéo de fond</Label>
                <Input
                  value={hero.video_url || ''}
                  onChange={(e) => setHero({ ...hero, video_url: e.target.value })}
                  placeholder="https://assets.mixkit.co/videos/..."
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Texte bouton principal</Label>
                  <Input
                    value={hero.cta_text || ''}
                    onChange={(e) => setHero({ ...hero, cta_text: e.target.value })}
                    placeholder="Demander un devis"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texte bouton secondaire</Label>
                  <Input
                    value={hero.secondary_cta_text || ''}
                    onChange={(e) => setHero({ ...hero, secondary_cta_text: e.target.value })}
                    placeholder="Découvrir nos services"
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card className="bg-slate-900 text-white overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Eye className="w-4 h-4" /> Aperçu en direct
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {hero?.title ? (
              hero.title.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return (
                    <span key={i} className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-orange-400">
                      {part.slice(2, -2)}
                    </span>
                  );
                }
                return part;
              })
            ) : (
              <span className="text-slate-400">Votre titre ici...</span>
            )}
          </h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            {hero?.subtitle ? (
              hero.subtitle.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return (
                    <span key={i} className="text-cyan-400 font-semibold">
                      {part.slice(2, -2)}
                    </span>
                  );
                }
                return part;
              })
            ) : (
              <span className="text-slate-500">Votre sous-titre ici...</span>
            )}
          </p>
          <div className="flex gap-3 justify-center">
            <span className="bg-cyan-500 text-white px-4 py-2 rounded-full text-sm">
              {hero?.cta_text || 'Bouton principal'}
            </span>
            <span className="border border-white/30 text-white px-4 py-2 rounded-full text-sm">
              {hero?.secondary_cta_text || 'Bouton secondaire'}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Statistiques</CardTitle>
              <CardDescription>Chiffres clés affichés dans le Hero</CardDescription>
            </div>
            <Button onClick={addStat} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(hero?.stats || []).map((stat, idx) => (
              <div key={idx} className="flex gap-3 items-center">
                <Input
                  value={stat.value}
                  onChange={(e) => {
                    const newStats = [...hero.stats];
                    newStats[idx].value = e.target.value;
                    setHero({ ...hero, stats: newStats });
                  }}
                  placeholder="150+"
                  className="w-24"
                />
                <Input
                  value={stat.label}
                  onChange={(e) => {
                    const newStats = [...hero.stats];
                    newStats[idx].label = e.target.value;
                    setHero({ ...hero, stats: newStats });
                  }}
                  placeholder="Projets réalisés"
                  className="flex-1"
                />
                <Button onClick={() => removeStat(idx)} variant="ghost" size="sm">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

export default CMSHeroPage;
